import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZardDropdownMenuComponent } from '@shared/components/ui/dropdown/dropdown.component';
import { ZardDropdownMenuItemComponent } from '@shared/components/ui/dropdown/dropdown-item.component';
import { firstValueFrom } from 'rxjs';
import { ApiService } from 'app/core/api/api.service';
import { ExchangeRateModalComponent } from './modal/currencies-modal.component';
import { ZardDialogService } from '@shared/components/ui/dialog/dialog.service';
import { ZardAlertDialogService } from '@shared/components/ui/alert-dialog/alert-dialog.service';
import { TranslocoPipe } from '@ngneat/transloco';
export interface Currency {
    id: number;
    code: string;
    displayCode: string;
    description: string;
    default: boolean;
}

export interface CurrencyRate {
    docDate: string;
    createdDate: string;
    currency: Currency;
    exchangeRate: number;
}

@Component({
    selector: 'currencies',
    standalone: true,
    imports: [CommonModule, TranslocoPipe, ZardDropdownMenuComponent, ZardDropdownMenuItemComponent],
    template: `
        <div class="flex items-center h-[56px] space-x-2">
            <z-dropdown-menu>
                <button dropdown-trigger class="min-w-0 w-fit h-8 px-2 text-[14px] text-gray-100 h-[48px]">
                    1 {{ selectedCurrency?.description }}
                </button>

            

                @for (currency of nonDefaultCurrencies; track currency.id) {
                    <z-dropdown-menu-item (click)="selectCurrency(currency)">
                        {{ currency.description }}
                    </z-dropdown-menu-item>
                }
            </z-dropdown-menu>
            <span class="text-gray-100 text-[14px]">=</span>

            @if(selectedCurrency) {
                <ng-container>
                @if(currentRate) {
                    <ng-container>
                        <span
                            class="cursor-pointer font-semibold text-sky-300 hover:text-sky-200 text-[14px] transition-colors px-1"
                            (click)="openExchangeRateModal()"
                        >
                            {{ currentRate.exchangeRate | number : '1.2-2' }}
                        </span>
                        <span class="text-gray-100 text-[14px]">{{
                            defaultCurrency?.description
                        }}</span>
                    </ng-container>
                } @else {
                    <ng-template>
                        <span
                            class="cursor-pointer text-red-300 hover:text-red-200 text-[14px] px-1"
                            (click)="openExchangeRateModal()"
                        >
                            {{ 'financial.exchange.setRate' | transloco }}
                        </span>
                        <span class="text-gray-100 text-[14px]">{{
                            defaultCurrency?.description
                        }}</span>
                    </ng-template>
                }
            </ng-container>
            }
            
        </div>
    `,
})
export class CurrenciesComponent implements OnInit {
    availableCurrencies: Currency[] = [];
    defaultCurrency: Currency | null = null;
    selectedCurrency: Currency | null = null;
    currentRate: CurrencyRate | null = null;

    get nonDefaultCurrencies(): Currency[] {
        return this.availableCurrencies.filter((c) => !c.default);
    }

    constructor(private _apiService: ApiService) {}

    async ngOnInit() {
        await this.loadCurrencies();
        await this.loadLatestRate();
    }

    private async loadCurrencies() {
        const response: any = await firstValueFrom(
            this._apiService.post('admin/currency/all?skipPagination=true', [])
        );

        this.availableCurrencies = response.content;
        this.defaultCurrency =
            this.availableCurrencies.find((c) => c.default) ?? null;
        this.selectedCurrency =
            this.availableCurrencies.find((c) => !c.default) ?? null;
    }

    private async loadLatestRate() {
        if (!this.selectedCurrency) return;
        const response: any = await firstValueFrom(
            this._apiService.get(
                `admin/currency-rate/currency/${this.selectedCurrency?.id}`
            )
        );

        this.currentRate = {
            exchangeRate: response,
            currency: this.selectedCurrency,
            docDate: '',
            createdDate: '',
        };
    }

    async selectCurrency(currency: Currency) {
        this.selectedCurrency = currency;
        await this.loadLatestRate();
    }

    private readonly _zDialog = inject(ZardDialogService);
    private readonly _zAlert = inject(ZardAlertDialogService);

    openExchangeRateModal() {
        if (!this.selectedCurrency) return;

        const dialogRef = this._zDialog.create<{ rateForm: { value: { rate: number } } }>({
            zTitle: undefined,
            zContent: ExchangeRateModalComponent,
            zData: {
                fromCurrency: this.selectedCurrency,
                toCurrency: this.defaultCurrency,
                currentRate: this.currentRate?.exchangeRate,
            },
            zOkText: 'Update',
            zOnOk: async (instance) => {
                const rate = instance.rateForm?.value?.rate;
                if (rate == null) return false;

                this._zAlert.info({
                    zTitle: 'Confirm change',
                    zDescription: `Set 1 ${this.selectedCurrency.displayCode} = ${Number(rate).toFixed(2)} ${this.defaultCurrency?.displayCode}?`,
                    zOkText: 'Confirm',
                    zOnOk: async () => {
                        await this.submitNewRate(rate);
                        await this.loadLatestRate();
                    },
                });

                return false;
            },
            zWidth: '400px',
        });
    }

    private async submitNewRate(rate: number) {
        if (!this.selectedCurrency) return;

        const today = new Date();
        const docDate = `${today.getFullYear()}-${String(
            today.getMonth() + 1
        ).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

        const payload = {
            docDate,
            currencyId: this.selectedCurrency.id,
            exchangeRate: rate,
            notes: `Updated via navbar component`,
        };

        await firstValueFrom(
            this._apiService.post('admin/currency-rate-doc', payload)
        );
    }
}
